import { useState, useEffect, Fragment } from "react";

import {
  Typography,
  InfoPanel,
  Switch,
  Label,
  Button,
  Badge,
  DocumentList,
  TextArea,
  Modal,
  SuccessModalContent,
} from "@king-ict/eupisi-ui/components";
import { ReactComponent as UnlockSuccessIcon } from "@king-ict/eupisi-ui/components/Icon/SuccessRequest.svg";
import { ReactComponent as Unlock } from "@king-ict/eupisi-ui/components/Icon/Unlock.svg";
import { useInput } from "@king-ict/eupisi-ui/hooks";
import { DocumentData } from "@king-ict/eupisi-ui/models";
import {
  nameof,
  isEmpty,
  getStatusColor,
  hrDateWithTime,
} from "@king-ict/eupisi-ui/utils";
import { Col, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useParams, useHistory, useRouteMatch } from "react-router";

import {
  getRequestCriterionDataByRequestId as getRequestCriterionDataByRequestIdApi,
  evaluateRequest as evaluateRequestApi,
  unlockRequest as unlockRequestApi,
  getCriterionDocument as getCriterionDocumentApi,
  cancelRequestEvaluation as cancelRequestEvaluationApi,
  getRequestBasicInfo as getRequestBasicInfoApi,
  sendEmails as sendEmailsApi
} from "../../../../api";
import {
  requestsRoute,
  subsequentRequestsRoute,
  requestDetailsRoute,
  subsequentRequestDetailsRoute,
  preschoolRequestsRoute,
} from "../../../../app";
import { MatchParams } from "../../../../components/Sidebar";
import { useBusinessRule } from "../../../../hooks";
import {
  RequestCriterion,
  UnlockReason,
  RequestCriterionData,
  RequestCriterionIntegrationFetchStatusEnum,
  RequestBasicInfo,
} from "../../../../models";
import { RequestPersonInfo } from "../../../../models";
import IntegrationEnum from "../../../../models/Integration/IntegrationEnum";
import { UnlockRequest } from "../../../../models/Request/UnlockRequest";
import ChildrenOfParentsCriterionData from "./ChildrenOfParentsCriterionData";
import PermanentAndTemporaryResidenceData from "./PermanentAndTemporaryResidenceData";
import styles from "./RequestCriteriaPage.module.scss";

export interface RequestCriteriaPageProps {
  requestPersonInfo: RequestPersonInfo;
}

export const RequestCriteriaPage = ({
  requestPersonInfo,
}: RequestCriteriaPageProps) => {
  const { requestId } = useParams<{ requestId: string }>();
  const [requestBasicInfo, setRequestBasicInfo] = useState<RequestBasicInfo>({} as RequestBasicInfo );
  const { t } = useTranslation();
  const requestMatch = useRouteMatch<MatchParams>(requestDetailsRoute);
  const subsequentRequestMatch = useRouteMatch<MatchParams>(
    subsequentRequestDetailsRoute
  );
  
  const history = useHistory();
  const { canEvaluateRequest, canUnlockRequest, canCancelRequestEvaluation } = useBusinessRule();

  const [
    requestCriterionData,
    setRequestCriterionData,
  ] = useState<RequestCriterionData>({} as RequestCriterionData);

  const [unlockModalOpen, setUnlockModalOpen] = useState<boolean>(false);

  const [aplicantParentEmailSend, setAplicantParentEmailSend] = useState<boolean>(false);
  const [otherParentEmailSend, setOtherParentEmailSend] = useState<boolean>(false);
  const [problemWithEmailSending, setProblemWithEmailSending] = useState<boolean>(false);

  const [unlockSuccessModalOpen, setUnlockSuccessModalOpen] = useState<boolean>(
    false
  );

  const [openEvaluateModal, setOpenEvaluateModal] = useState(false);
  const [openEvaluateSuccessModal, setOpenEvaluateSuccessModal] = useState(
    false
  );
  const [openCancelEvaluationModal, setOpenCancelEvaluationModal] = useState(false);
  const [openCancelEvaluationSuccessModal, setOpenCancelEvaluationSuccessModal] = useState(
    false
  );

  const [unlockReason, setUnlockReason] = useState<UnlockReason>(
    {} as UnlockReason
  );

  const { handleChange } = useInput();

  const emptyValue = "-";

  const handleUnlockClicked = () => {
    handleToggleUnlockModalOpen();
  };

  const handleToggleUnlockModalOpen = () => {
    setUnlockModalOpen((prevUnlockModalOpen) => !prevUnlockModalOpen);
  };

  const handleToggleUnlockSuccessModalOpen = () => {
    setUnlockSuccessModalOpen((prev) => !prev);
  };

  const handleUnlockConfirmed = () => {   
    
    const unloackRequest : UnlockRequest = {
      requestCriterion: requestCriterionData.criteria.filter((x) => x.isProven),
      reason: unlockReason.reason
    };

    (async () => await unlockRequestApi(unloackRequest, +requestId))()
      .then(async () => {await sendEmails()
        .then(() => {
          handleToggleUnlockModalOpen();
          handleToggleUnlockSuccessModalOpen();
      })
    });
  };

  const sendEmails = async () => {
    try {
      await sendEmailsApi(Number(+requestId)).then(() => 
      handleEmailMessage()
      );     
    }
    catch (error) {
        setProblemWithEmailSending(true);
    }    
  };

  const handleEmailMessage = () => {
    if (requestBasicInfo?.applicantParent?.email) {
      setAplicantParentEmailSend(true);
    }
    if (requestBasicInfo?.otherParent?.email) {
      setOtherParentEmailSend(true);
    }
    if (!requestBasicInfo?.applicantParent?.email && !requestBasicInfo?.otherParent?.email) {
      setProblemWithEmailSending(true);
    }
  }

  const handleUnlockSuccessModalClose = () => {
    handleToggleUnlockSuccessModalOpen();
  };

  const handlePushToRoute = () => {
    history.push(
      requestMatch?.path === requestDetailsRoute
        ? requestsRoute
        : subsequentRequestMatch?.path === subsequentRequestDetailsRoute
        ? subsequentRequestsRoute
        : preschoolRequestsRoute
    );
  };
  useEffect(() => {
    (async () => {
      if (requestId && isEmpty(requestCriterionData)) {
        const newRequestCriterionData = await getRequestCriterionDataByRequestIdApi(
          +requestId
        );
        setRequestCriterionData(newRequestCriterionData);
      }
    })();
  }, [requestId, requestCriterionData]);

  useEffect(() => {
    (async () => {
      if (requestId && isEmpty(requestBasicInfo)) {
        const requestBasicInfoData = await getRequestBasicInfoApi(+requestId);
        setRequestBasicInfo(requestBasicInfoData);
      }
    })();
  }, [requestId, requestBasicInfo]);

  const handleToggleEvaluateModal = () => {
    setOpenEvaluateModal((openEvaluateModal) => !openEvaluateModal);
  };
  
  const handleToggleCancelEvaluationModal = () => {
    setOpenCancelEvaluationModal((openCancelEvaluationModal) => !openCancelEvaluationModal);
  };

  const getCriterionExternalStatusColor = (
    fetchStatus: RequestCriterionIntegrationFetchStatusEnum
  ) => {
    switch (fetchStatus) {
      case RequestCriterionIntegrationFetchStatusEnum.Unknown:
        return "warning";
      case RequestCriterionIntegrationFetchStatusEnum.Success:
        return "success";
      case RequestCriterionIntegrationFetchStatusEnum.Fail:
        return "danger";
    }
  };

  const getCriterionExternalStatusName = (
    fetchStatus: RequestCriterionIntegrationFetchStatusEnum
  ) => {
    switch (fetchStatus) {
      case RequestCriterionIntegrationFetchStatusEnum.Unknown:
        return "Nepoznato";
      case RequestCriterionIntegrationFetchStatusEnum.Success:
        return "Uspješno";
      case RequestCriterionIntegrationFetchStatusEnum.Fail:
        return "Neuspješno";
    }
  };

  const calculateTotalPoints = () => {
    let totalPoints = 0;
    requestCriterionData.criteria
      ?.filter((x) => x.isProven)
      .forEach((criteria) => (totalPoints += criteria.points));

    return totalPoints;
  };

  const handleCriterionChange = (criterionId: number) => {
    setRequestCriterionData((prev) => {
      const currentCriteria = prev.criteria ? [...prev.criteria] : [];
      const newCriteria: RequestCriterion[] = [];
      currentCriteria.forEach((cr) => {
        if (cr.criterionId === criterionId) {
          newCriteria.push({ ...cr, isProven: !cr.isProven });
        } else {
          newCriteria.push({ ...cr });
        }
      });
      return { ...prev, criteria: newCriteria };
    });
  };

  const handleToggleEvaluateSuccessModal = () => {
    setOpenEvaluateSuccessModal(
      (openEvaluateSuccessModal) => !openEvaluateSuccessModal
    );
  };

  const handleEvaluate = async () => {
    setOpenEvaluateModal(false);

    await evaluateRequestApi(
      requestCriterionData.criteria.filter((x) => x.isProven),
      +requestId
    ).then(() => {
      handleToggleEvaluateSuccessModal();
    });
  };
  const handleToggleCancelEvaluationSuccessModal = () => {
    setOpenCancelEvaluationSuccessModal(
      (openCancelEvaluationSuccessModal) => !openCancelEvaluationSuccessModal
    );
  };

  const handleCancelEvaluation = async () => {
    setOpenCancelEvaluationModal(false);

    await cancelRequestEvaluationApi(+requestId).then(() => {
      handleToggleCancelEvaluationSuccessModal();
    });
  }

  const handleBackToRequests = () => {
    handleToggleEvaluateSuccessModal();
    handlePushToRoute();
  };

  const handleDownloadCriteriaDocument = (document: DocumentData) => {
    if (document.fileID) {
      getCriterionDocumentApi(document.fileID);
    }
  };

  const getTotalPoints = () => {
    const isDirect = requestCriterionData?.criteria?.some(
      (x) => x.isProven && x.isDirectEnrollment
    );

    return (
      <>
        {!isDirect ? (
          <Col md={12} className={`${styles.withoutPadding}`}>
            <Badge
              className={styles.pointsBadge}
              value={calculateTotalPoints()}
              variant="big"
              color="success"
            />

            <Typography
              color="success"
              variant="XXLargeBold"
              component={"span"}
            >
              {t("request.criteria.totalPointsScored")}
            </Typography>
          </Col>
        ) : (
          <Typography color="success" variant="XXLargeBold" component={"span"}>
            {t("criterion.directEnrollment")}
          </Typography>
        )}
      </>
    );
  };

  const getCriteriaItems = () => {
    return (
      <>
        {requestCriterionData.criteria?.map((criterion) => (
          <Fragment key={criterion.id}>
            <Row md={12} className={styles.criterion}>
              <Col md={6} xs={9} className={styles.withoutPadding}>
                <Switch
                  name={nameof<typeof criterion>("isProven")}
                  selected={criterion.isProven}
                  disabled={
                    !canEvaluateRequest(
                      requestPersonInfo.requestPedagogicalYearId,
                      requestPersonInfo.requestStatusId,
                      requestPersonInfo.enrollmentTypeId,
                      requestPersonInfo.monthId
                    )
                  }
                  label={criterion.criterionName}
                  ariaLabel={criterion.criterionName}
                  color="base"
                  onChange={() => handleCriterionChange(criterion.criterionId)}
                />
              </Col>
              {criterion.isProven && (
                <Col
                  md={6}
                  xs={3}
                  className={`${styles.withoutPadding} ${styles.pointsBadgeLayout}`}
                >
                  {criterion.isDirectEnrollment ? (
                    <Typography color="base" variant="base" component={"span"}>
                      {t("criterion.directEnrollment")}
                    </Typography>
                  ) : (
                    <>
                      <Badge
                        className={styles.pointsBadge}
                        value={criterion.points}
                        variant="small"
                        color="success"
                      />
                      <Typography
                        color="base"
                        variant="base"
                        component={"span"}
                      >
                        {t("request.criteria.points")}
                      </Typography>
                    </>
                  )}
                </Col>
              )}
            </Row>
            {criterion?.criterionIntegrationData && (
              <Col md={12} className={styles.withoutPadding}>
                <Typography
                  variant="mediumBold"
                  color="base"
                  className={styles.spaceClass}
                >
                  {t("request.criteria.externInformation")}
                </Typography>

                {criterion?.criterionIntegrationData?.applicantParentData && (
                  <Row>
                  <Col md={6}>                    
                    <Typography variant="baseBold">
                       {t("request.criteria.applicantParent")}
                    </Typography>              
                    {criterion?.criterionIntegrationData?.applicantParentData?.data && (
                      getIntegrationData(criterion.criterionIntegrationData.applicantParentData.data, criterion.integrationId)
                    )}
                    </Col>
                    {criterion?.criterionIntegrationData?.applicantParentData && (
                      <Col md={6}>
                      <div className={styles.requestStatus}>
                        <Label
                          color={getCriterionExternalStatusColor(
                            criterion.criterionIntegrationData?.applicantParentData
                              ?.fetchStatus |
                              RequestCriterionIntegrationFetchStatusEnum.Unknown
                          )}
                        >
                          {getCriterionExternalStatusName(
                            criterion.criterionIntegrationData?.applicantParentData
                              ?.fetchStatus |
                              RequestCriterionIntegrationFetchStatusEnum.Unknown
                          )}
                        </Label>
                      </div>
                    </Col>
                    )}                    
                </Row>
                )}
                              
                {criterion?.criterionIntegrationData?.otherParentData && (
                  <Row>
                  <Col md={6}>                    
                    <Typography variant="baseBold">
                      {t("request.criteria.otherParent")}
                    </Typography>              
                    {criterion?.criterionIntegrationData?.otherParentData?.data && (
                      getIntegrationData(criterion.criterionIntegrationData.otherParentData.data, criterion.integrationId)
                    )}
                  </Col>
                  {criterion?.criterionIntegrationData?.otherParentData.fetchStatus && (
                    <Col md={6}>
                    <div className={styles.requestStatus}>
                      <Label
                        color={getCriterionExternalStatusColor(
                          criterion.criterionIntegrationData?.otherParentData
                            ?.fetchStatus |
                            RequestCriterionIntegrationFetchStatusEnum.Unknown
                        )}
                      >
                        {getCriterionExternalStatusName(
                          criterion.criterionIntegrationData?.otherParentData
                            ?.fetchStatus |
                            RequestCriterionIntegrationFetchStatusEnum.Unknown
                        )}
                      </Label>
                    </div>
                  </Col>
                  )}
              </Row>
                )}
                
                {criterion?.criterionIntegrationData?.childData && (
                  <Row>
                <Col md={6}>
                   <Typography variant="baseBold">
                      {t("request.criteria.child")}
                    </Typography>              
                     {criterion?.criterionIntegrationData?.childData?.data && (
                        getIntegrationData(criterion.criterionIntegrationData.childData.data, criterion.integrationId)
                  )}
                </Col>
                {criterion?.criterionIntegrationData?.childData.fetchStatus && (
                  <Col md={6}>
                  <div className={styles.requestStatus}>
                    <Label
                      color={getCriterionExternalStatusColor(
                        criterion.criterionIntegrationData?.childData
                          ?.fetchStatus |
                          RequestCriterionIntegrationFetchStatusEnum.Unknown
                      )}
                    >
                      {getCriterionExternalStatusName(
                        criterion.criterionIntegrationData?.childData
                          ?.fetchStatus |
                          RequestCriterionIntegrationFetchStatusEnum.Unknown
                      )}
                    </Label>
                  </div>
                </Col>
                )}
              </Row>
                )}
              
            </Col>            
            )}
            <Col
              md={12}
              className={`${styles.withoutPadding} ${styles.rowSpace}`}
            >
              <Typography
                variant="mediumBold"
                color="base"
                className={styles.documentSpaceClass}
              >
                {t("request.criteria.documentsAdded")}
              </Typography>
              {criterion.documents && criterion.documents.length > 0 ? (
                <DocumentList
                  onDocumentClick={handleDownloadCriteriaDocument}
                  color="primary"
                  documents={criterion.documents}
                  fullWidth
                  removable={false}
                />
              ) : (
                <Typography variant="base" color="light">
                  {t("request.criteria.noDocuments")}
                </Typography>
              )}
            </Col>
            <hr className={styles.line} />
          </Fragment>
        ))}
      </>
    );
  };

  const getRequestComments = () => {
    if (requestCriterionData.requestComments?.length > 0) {
      return (
        <>
          <Typography
            className={styles.unlockReason}
            variant="mediumBold"
            component="h3"
          >
            {t("request.criteria.unlockReasonLabel")}
          </Typography>
          {requestCriterionData.requestComments.map((comment) => {
            return (
              <TextArea
                key={comment.id}
                label={hrDateWithTime(comment.dateCreated)}
                ariaLabel={hrDateWithTime(comment.dateCreated)}
                value={comment.comment}
                disabled
                readonly
              ></TextArea>
            );
          })}
        </>
      );
    }
  };

  const getIntegrationData = (data: string, integrationId?: number) => {
    switch (integrationId) {
      case IntegrationEnum.MUP1:
      case IntegrationEnum.MUP2:
      case IntegrationEnum.MUP3:
            return <PermanentAndTemporaryResidenceData
              data={data}
            />

       case IntegrationEnum.ParentChild:
            return <ChildrenOfParentsCriterionData
              data={data}
            />
     
      default:
        return <Row>
          <Col md={12}>
            <Typography>{data}</Typography>
          </Col>
        </Row>
    }
  }

  return (
    <>
      <Row className={`${styles.rowHeadingSpace}`}>
        <Col xs={12} className={styles.withoutPadding}>
          <InfoPanel
            message={t("request.criteria.infoMessage")}
            color="success"
            fullWidth
          />
        </Col>
      </Row>
      <Row>
        <Col md={12}>
          <Typography variant="largeBold">
            {t("sidebar.criteriaAndPoints")}
          </Typography>
        </Col>
      </Row>
      <Row>
        <Col md={4}>
          <Typography
            variant="mediumBold"
            color="base"
            className={styles.spaceClass}
          >
            {t("common.status")}
          </Typography>
          <div className={styles.requestStatus}>
            <Label color={getStatusColor(requestCriterionData.requestStatusId)}>
              {requestCriterionData.requestStatusName}
            </Label>
          </div>
        </Col>
      </Row>
      <Row className={styles.rowSpace}>
        <Col md={4} className={styles.withoutPadding}>
          <Typography
            variant="mediumBold"
            color="base"
            className={styles.documentSpaceClass}
          >
            {t("request.criteria.applicantEmail")}
          </Typography>
          <Typography variant="base" color="light">
            {requestCriterionData.applicantEmail}
          </Typography>
        </Col>
        <Col md={4} className={styles.withoutPadding}>
          <Typography
            variant="mediumBold"
            color="base"
            className={styles.documentSpaceClass}
          >
            {t("request.criteria.applicantPhone")}
          </Typography>
          <Typography variant="base" color="light">
            {requestCriterionData.applicantPhone}
          </Typography>
        </Col>
      </Row>
      <hr className={styles.line} />
      <Row className={`${styles.withoutMargin} ${styles.subHeader}`}>
        <Col md={12} className={styles.withoutPadding}>
          <Typography variant="mediumBold">{t("sidebar.criteria")}</Typography>
        </Col>
      </Row>
      <Row className={styles.withoutMargin}>
        <Col md={12} className={styles.withoutPadding}>
          {getCriteriaItems()}
          <Col xs={12} className={styles.withoutPadding}>
            <Col md={12} className={styles.withoutPadding}>
              <Typography variant="mediumBold" className={styles.commentSpace}>
                {t("common.comment")}
              </Typography>
            </Col>
            <TextArea
              name={nameof<typeof requestCriterionData>("criterionComment")}
              value={requestCriterionData?.criterionComment ?? ""}
              label=""
              ariaLabel=""
              maxLength={700}
              rows={3}
              readonly
              disabled
            />
          </Col>
          <hr className={styles.line} />
          {getTotalPoints()}
          {getRequestComments()}
          <Row className={`${styles.withoutPadding} ${styles.buttonsLayout}`}>
            <Col
              md={12}
              className={`${styles.withoutPadding} ${styles.buttonsRight}`}
            >
              {canUnlockRequest(
                requestPersonInfo.requestPedagogicalYearId,
                requestPersonInfo.requestStatusId,
                requestPersonInfo.enrollmentTypeId,
                requestPersonInfo.monthId
              ) && (
                <Button
                  size="sm"
                  color="primary"
                  className={styles.buttonSpace}
                  outline
                  icon={<Unlock />}
                  onClick={handleUnlockClicked}
                >
                  {t("request.criteria.unlockRequest")}
                </Button>
              )}
              {canEvaluateRequest(
                requestPersonInfo.requestPedagogicalYearId,
                requestPersonInfo.requestStatusId,
                requestPersonInfo.enrollmentTypeId,
                requestPersonInfo.monthId
              ) && (
                <Button
                  size="sm"
                  color="primary"
                  className={styles.buttonSpace}
                  onClick={handleToggleEvaluateModal}
                >
                  {t("request.criteria.evaluateRequest")}
                </Button>
              )} 
              {canCancelRequestEvaluation(
                requestPersonInfo.requestPedagogicalYearId,
                requestPersonInfo.requestStatusId,
                requestPersonInfo.enrollmentTypeId,
                requestPersonInfo.monthId
              ) && (
                <Button
                  size="sm"
                  color="primary"
                  className={styles.buttonSpace}
                  onClick={handleToggleCancelEvaluationModal}
                >
                  {t("request.criteria.cancelRequestEvaluation")}
                </Button>
              )}
            </Col>
          </Row>
          <hr className={styles.lineEnd} />
        </Col>
      </Row>
      <Modal
        open={unlockModalOpen}
        onClose={handleToggleUnlockModalOpen}
        title={t("request.criteria.unlockConfirmModalTitle")}
        showCloseMark={false}
        variant={"big"}
      >
        <TextArea
          type="text"
          label={t("request.criteria.unlockReasonLabel")}
          ariaLabel={t("request.criteria.unlockReasonLabel")}
          maxLength={2000}
          rows={12}
          value={unlockReason.reason || ""}
          name={nameof<typeof unlockReason>("reason")}
          onChange={(e) => handleChange(e, setUnlockReason)}
          required
        />
        <div className={styles.confirmButtonsContainer}>
          <div>
            <Button outline onClick={handleToggleUnlockModalOpen}>
              {t("common.quit")}
            </Button>
          </div>
          <div>
            <Button
              disabled={
                unlockReason.reason == null || unlockReason.reason.length === 0
              }
              onClick={handleUnlockConfirmed}
            >
              {t("request.criteria.unlockConfirm")}
            </Button>
          </div>
        </div>
      </Modal>
      <Modal
        type="message"
        open={unlockSuccessModalOpen}
        confirmText={t("common.close")}
        onConfirm={handleUnlockSuccessModalClose}
        onClose={handleUnlockSuccessModalClose}
      >
        <div className={styles.modalMessageContent}>
          <UnlockSuccessIcon className={styles.requestIcon} />
          <Typography variant="largeBold">
            {t("request.criteria.requestSuccessfullyUnlocked")}
          </Typography>
        </div>
        {aplicantParentEmailSend && (
        <Row className={`${styles.rowHeadingSpace}`}>
          <Col xs={12} className={styles.withoutPadding}>
          <hr/>
            <InfoPanel
              message={t("request.criteria.emalToParentSuccessfullySended", {
                firstName: requestBasicInfo?.applicantParent?.firstName,
                lastName: requestBasicInfo?.applicantParent?.lastName,
                email: requestBasicInfo?.applicantParent?.email,
            })}
              color="success"
              fullWidth
            />
          </Col>
        </Row>
        )}
        {otherParentEmailSend && (
          <Row className={`${styles.rowHeadingSpace}`}>
            <Col xs={12} className={styles.withoutPadding}>
            <hr/>
              <InfoPanel
                message={t("request.criteria.emalToParentSuccessfullySended", {
                  firstName: requestBasicInfo?.otherParent?.firstName,
                  lastName: requestBasicInfo?.otherParent?.lastName,
                  email: requestBasicInfo?.otherParent?.email,
              })}
                color="success"
                fullWidth
              />
            </Col>
        </Row>
        )}
        {problemWithEmailSending && (
          <Row className={`${styles.rowHeadingSpace}`}>
          <Col xs={12} className={styles.withoutPadding}>
          <hr/>
            <InfoPanel
              message={t("request.criteria.emalToParentNotSended")}
              color="warning"
              fullWidth
            />
          </Col>
      </Row>
        )}
      </Modal>
      <Modal
        type={"confirm"}
        title={t("request.criteria.evaluateRequestConfirmModalTitle")}
        open={openEvaluateModal}
        confirmText={t("request.criteria.confirmEvaluateText")}
        cancelText={t("common.no")}
        onClose={handleToggleEvaluateModal}
        onConfirm={handleEvaluate}
      >
        <div>{t("request.criteria.confirmEvaluateMessage")}</div>
      </Modal>

      <Modal
        type={"message"}
        open={openEvaluateSuccessModal}
        confirmText={t("common.close")}
        onConfirm={handleBackToRequests}
        onClose={() => handleToggleEvaluateSuccessModal()}
      >
        <SuccessModalContent
          message={t("request.criteria.evaluateRequestSuccesfullMessage")}
        />
      </Modal>
      <Modal
        type={"confirm"}
        title={t("request.criteria.cancelRequestEvaluationConfirmModalTitle")}
        open={openCancelEvaluationModal}
        confirmText={t("request.criteria.confirmCancelEvaluationText")}
        cancelText={t("common.no")}
        onClose={handleToggleCancelEvaluationModal}
        onConfirm={handleCancelEvaluation}
      >
        <div>{t("request.criteria.confirmCancelRequestEvaluationMessage")}</div>
      </Modal>
      <Modal
        type={"message"}
        open={openCancelEvaluationSuccessModal}
        confirmText={t("common.close")}
        onConfirm={handleBackToRequests}
        onClose={() => handleToggleCancelEvaluationSuccessModal()}
      >
        <SuccessModalContent
          message={t("request.criteria.cancelRequestEvaluationSuccesfullMessage")}
        />
      </Modal>
    </>
  );
};
